import { useState, useCallback } from 'react';
import { Discount, DiscountType } from '../types';

// No predefined discounts - only manual discounts allowed
const MOCK_DISCOUNTS: Discount[] = [];

const getStoredDiscounts = (): Discount[] => {
  try {
    // Clear any existing predefined discounts for security
    localStorage.removeItem('discounts');
  } catch (error) {
    console.error('Error clearing stored discounts:', error);
  }
  return MOCK_DISCOUNTS; // Returns empty array
};

const saveDiscounts = (discounts: Discount[]) => {
  try {
    localStorage.setItem('discounts', JSON.stringify(discounts));
  } catch (error) {
    console.error('Error saving discounts:', error);
  }
};

export const useDiscounts = () => {
  const [discounts, setDiscounts] = useState<Discount[]>(() => getStoredDiscounts());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getActiveDiscounts = useCallback(() => {
    return discounts.filter(discount => discount.isActive);
  }, [discounts]);

  const getDiscountById = useCallback((id: string) => {
    return discounts.find(discount => discount.id === id);
  }, [discounts]);

  const createDiscount = useCallback(async (discountData: Omit<Discount, 'id' | 'createdAt' | 'updatedAt'>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const newDiscount: Discount = {
        ...discountData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      const updatedDiscounts = [...discounts, newDiscount];
      setDiscounts(updatedDiscounts);
      saveDiscounts(updatedDiscounts);
      return newDiscount;
    } catch (err) {
      setError('Error creating discount');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [discounts]);

  const updateDiscount = useCallback(async (id: string, discountData: Partial<Discount>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updatedDiscounts = discounts.map(discount =>
        discount.id === id
          ? { ...discount, ...discountData, updatedAt: new Date().toISOString() }
          : discount
      );
      setDiscounts(updatedDiscounts);
      saveDiscounts(updatedDiscounts);
    } catch (err) {
      setError('Error updating discount');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [discounts]);

  const deleteDiscount = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updatedDiscounts = discounts.filter(discount => discount.id !== id);
      setDiscounts(updatedDiscounts);
      saveDiscounts(updatedDiscounts);
    } catch (err) {
      setError('Error deleting discount');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [discounts]);

  const calculateDiscountAmount = useCallback((
    discountType: DiscountType,
    percentage: number | undefined,
    fixedAmount: number | undefined,
    subtotal: number
  ): number => {
    if (discountType === DiscountType.PERCENTAGE && percentage) {
      return subtotal * (percentage / 100);
    } else if (discountType === DiscountType.FIXED_AMOUNT && fixedAmount) {
      return Math.min(fixedAmount, subtotal); // Don't allow discount to exceed subtotal
    }
    return 0;
  }, []);

  return {
    discounts,
    isLoading,
    error,
    getActiveDiscounts,
    getDiscountById,
    createDiscount,
    updateDiscount,
    deleteDiscount,
    calculateDiscountAmount,
  };
};